import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ArrowLeft, ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export function LoginScreen({ role }) {
  const isMerchant = role === "merchant";
  const title = role === "merchant" ? "Merchant Portal" : role === "super_admin" ? "Super Admin Login" : "Branch Login";
  const subtitle = role === "merchant" ? "Manage your API key, webhooks & submissions."
    : role === "super_admin" ? "Sign in to the Super Admin console." : "Sign in to the branch dashboard.";
  const { login, verify2fa } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState("password"); // "password" | "totp"
  const [tempToken, setTempToken] = useState("");
  const [code, setCode] = useState("");

  const homes = { merchant: "/merchant", super_admin: "/super-admin", branch: "/branch" };

  const goHome = (u) => {
    if (u.role !== role) { toast.error("Wrong account type for this login page"); return; }
    nav(homes[u.role] || "/");
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email, password);
      if (u.requires_2fa) { setTempToken(u.temporary_token); setStep("totp"); return; }
      goHome(u);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const verify = async (e) => {
    e.preventDefault();
    if (code.length !== 6) return toast.error("Enter the 6-digit code");
    setBusy(true);
    try {
      const u = await verify2fa(tempToken, code);
      goHome(u);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const fieldCls = `mt-1 ${isMerchant ? "bg-[#0A0A0A] border-zinc-700 text-white" : "bg-white border-slate-300 text-slate-900"}`;
  const labelCls = `text-xs ${isMerchant ? "text-zinc-400" : "text-slate-500"}`;

  return (
    <div className={`min-h-screen flex flex-col items-center justify-center px-4 font-sans ${isMerchant ? "bg-[#0A0A0A] text-white" : "bg-[#F7F8FA] text-[#1F2937]"}`}>
      <Link to="/" className={`absolute top-6 left-6 text-sm inline-flex items-center gap-1 ${isMerchant ? "text-zinc-500 hover:text-white" : "text-slate-500 hover:text-slate-900"}`}>
        <ArrowLeft className="w-4 h-4" /> Customer Page
      </Link>
      <div className="w-full max-w-sm">
        <div className="flex justify-center mb-8">{isMerchant ? <Logo light /> : <Logo />}</div>
        <div className={`rounded-2xl p-6 ${isMerchant ? "border border-zinc-800 bg-[#18181B]" : "border border-slate-200 bg-white shadow-sm"}`}>
          {step === "password" ? (
            <>
              <h1 className={`text-xl font-bold tracking-tight ${isMerchant ? "" : "text-slate-900"}`}>{title}</h1>
              <p className={`text-sm mt-1 ${isMerchant ? "text-zinc-400" : "text-slate-500"}`}>{subtitle}</p>
              <form onSubmit={submit} className="mt-6 space-y-4">
                <div>
                  <Label className={labelCls}>Email</Label>
                  <Input data-testid="login-email-input" value={email} onChange={(e) => setEmail(e.target.value)} type="email" required className={fieldCls} />
                </div>
                <div>
                  <Label className={labelCls}>Password</Label>
                  <Input data-testid="login-password-input" value={password} onChange={(e) => setPassword(e.target.value)} type="password" required className={fieldCls} />
                </div>
                <Button data-testid="login-submit-button" type="submit" disabled={busy} className={`w-full h-11 text-white font-semibold rounded-lg ${isMerchant ? "bg-emerald-600 hover:bg-emerald-500" : "bg-teal-600 hover:bg-teal-700"}`}>
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign In"}
                </Button>
              </form>
            </>
          ) : (
            <>
              <div className={`flex items-center gap-2 ${isMerchant ? "text-emerald-400" : "text-teal-600"}`}>
                <ShieldCheck className="w-5 h-5" />
                <h1 className={`text-xl font-bold tracking-tight ${isMerchant ? "text-white" : "text-slate-900"}`}>Two-Factor Verification</h1>
              </div>
              <p className={`text-sm mt-1 ${isMerchant ? "text-zinc-400" : "text-slate-500"}`}>Enter the 6-digit code from your authenticator app.</p>
              <form onSubmit={verify} className="mt-6 space-y-4">
                <Input data-testid="login-2fa-input" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} required autoFocus className={`text-center text-lg tracking-[0.4em] font-mono ${fieldCls}`} />
                <Button data-testid="login-2fa-submit-button" type="submit" disabled={busy || code.length !== 6} className={`w-full h-11 text-white font-semibold rounded-lg ${isMerchant ? "bg-emerald-600 hover:bg-emerald-500" : "bg-teal-600 hover:bg-teal-700"}`}>
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Verify & Sign In"}
                </Button>
              </form>
              <button onClick={() => { setStep("password"); setCode(""); setTempToken(""); }} className={`mt-4 w-full text-center text-xs ${isMerchant ? "text-zinc-500 hover:text-zinc-300" : "text-slate-500 hover:text-slate-700"}`}>← Back to login</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
